import type { OverallRow } from '@airmail/engine';
import { cx } from '@/ui';
import { useT } from '@/i18n';
import type { TeamLookup } from '@/lib/teams';

/**
 * Gesamttabelle über alle Gruppen (Harald, 09/2026): „Die Tabelle über alle.
 * Reihenfolge: Punkte (gewonnene − verlorene Spiele), dann gewonnene Spiele,
 * verlorene Spiele und danach die Punktdifferenz der Einzelergebnisse aus in
 * allen Spielen erzielter Würfe."
 *
 * Gerechnet wird das in `overallStandings`; hier steht nur die Darstellung.
 * Die Spalte **Pkt** ist deshalb Siege − Niederlagen und kann von der Spalte
 * gleichen Namens in der Gruppentabelle abweichen, wenn das Regelwerk zwei
 * Punkte je Sieg vergibt — die Kopfzeile sagt es dazu.
 */
export function GesamtTabelle({ rows, lookup, compact, hervor = 0 }: { rows: OverallRow[]; lookup: TeamLookup; compact?: boolean; hervor?: number }) {
  const t = useT();
  const zelle = compact ? 'px-2 py-1' : 'px-2.5 py-1.5';
  return (
    <table className="w-full border-collapse tabular-nums">
      <thead>
        <tr className="label text-left">
          <th className={cx(zelle, 'w-10 text-right')}>#</th>
          <th className={zelle}>{rows.length > 0 && rows.every((r) => (lookup(r.teamId)?.players.length ?? 0) <= 1) ? t('Teilnehmer') : t('Team')}</th>
          <th className={cx(zelle, 'w-16')}>{t('Gruppe')}</th>
          <th className={cx(zelle, 'w-10 text-right')}>{t('Sp')}</th>
          <th className={cx(zelle, 'w-10 text-right')}>{t('S')}</th>
          <th className={cx(zelle, 'w-10 text-right')}>{t('N')}</th>
          <th className={cx(zelle, 'w-14 text-right')} title={t('Gewonnene minus verlorene Spiele')}>
            {t('Pkt')}
          </th>
          <th className={cx(zelle, 'w-16 text-right')} title={t('Differenz aller erzielten und kassierten Würfe')}>
            {t('Diff')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const team = lookup(r.teamId);
          return (
            <tr key={r.teamId} className={cx('border-t border-line', hervor > 0 && r.rank <= hervor && 'bg-panel2')}>
              <td className={cx(zelle, 'text-right text-dim')}>{r.rank}</td>
              <td className={cx(zelle, 'font-bold tracking-tight')}>{team?.name ?? '?'}</td>
              <td className={cx(zelle, 'text-dim')}>
                {r.groupName}
                <span className="text-xs"> · {r.groupRank}.</span>
              </td>
              <td className={cx(zelle, 'text-right text-dim')}>{r.played}</td>
              <td className={cx(zelle, 'text-right')}>{r.wins}</td>
              <td className={cx(zelle, 'text-right')}>{r.losses}</td>
              <td className={cx(zelle, 'text-right font-bold')}>{vorzeichen(r.points)}</td>
              <td className={cx(zelle, 'text-right')}>{vorzeichen(r.scoreDiff)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function vorzeichen(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}
